import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { CloudX } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { getDiffColor, getDiffDirectionSymbol, getPercentageDiffText } from '../helpers'
import Icon from '../../Icons'
import UpsellingBlocker from '../UpsellingBlocker'
import Can from '../../Can'
import DoughnutChart from '../../Charts/DoughnutChart/DoughnutChart'

const GiftCards = ({ marketingData }) => {
  const { t } = useTranslation(['building-analytics'])
  const isDougnutDataCorrect =
    marketingData &&
    marketingData.giftCards &&
    marketingData.giftCards.sendTotal.value &&
    marketingData.giftCards.sendTotal.percentageDiff !== '0.00%'

  const getGiftCardsGraph = () => {
    const CHART_COLORS = ['rgba(255, 99, 132, 1)', 'rgba(153, 102, 255, 1)']
    const CHART_LABEL = '# of Votes'

    const CHART_LABELS = [
      t('building-analytics:giftCards.redeemedLabel'),
      t('building-analytics:giftCards.unredeemedLabel'),
    ]
    const CHART_DATA = marketingData &&
      marketingData.giftCards && [
        marketingData.giftCards.redeemed.value,
        marketingData.giftCards.unredeemed.value,
      ]
    const CHART_DIFF = marketingData &&
      marketingData.giftCards && [
        marketingData.giftCards.redeemed.percentageDiff,
        marketingData.giftCards.unredeemed.percentageDiff,
      ]
    const CHART_DATA_VALUE = marketingData &&
      marketingData.giftCards && [
        marketingData.giftCards.redeemedValue.value,
        marketingData.giftCards.unredeemedValue.value,
      ]
    const CHART_DIFF_VALUE = marketingData &&
      marketingData.giftCards && [
        marketingData.giftCards.redeemedValue.percentageDiff,
        marketingData.giftCards.unredeemedValue.percentageDiff,
      ]

    const defaultDoughnutChartOptions = marketingData &&
      marketingData.giftCards && {
        plugins: {
          innerText: true,
          datalabels: {
            display: false,
          },
          doughnutlabel: {
            labels: [
              {
                text: marketingData.giftCards.sendTotal.value,
                font: {
                  size: '35',
                  weight: '500',
                },
                color: 'black',
              },
              {
                text: `${getPercentageDiffText(marketingData.giftCards.sendTotal)}`,
                font: {
                  size: '15',
                  weight: '500',
                },
                color: '#548671',
              },
            ],
          },
        },
        tooltips: {
          callbacks: {
            label: (tooltipItem, data) => ` ${data.labels[tooltipItem['index']]}`,
            afterLabel: function (tooltipItem, data) {
              let dataset = data['datasets'][0]

              return ` ${dataset.data[tooltipItem['index']]} ${getDiffDirectionSymbol(
                dataset.diff[tooltipItem['index']],
              )} ${dataset.diff[tooltipItem['index']]}
 $${dataset.dataValue[tooltipItem['index']]} ${getDiffDirectionSymbol(
   dataset.diffValue[tooltipItem['index']],
 )} ${dataset.diffValue[tooltipItem['index']]}`
            },
          },
          caretSize: 0,
          backgroundColor: 'rgba(255,255,255,0.9)',
          titleFontSize: 16,
          borderColor: '#EAE9E9',
          cornerRadius: 6,
          borderWidth: 1,
          titleFontColor: '#0066ff',
          bodyFontColor: '#000',
          bodyFontSize: 14,
        },
      }

    const tooltipCallbacks = {
      label: (tooltipItem, data) => ` ${data.labels[tooltipItem['index']]}`,
      afterLabel: function (tooltipItem, data) {
        let dataset = data['datasets'][0]
        return ` ${dataset.data[tooltipItem['index']]} ${getDiffDirectionSymbol(
          dataset.diff[tooltipItem['index']],
        )} ${dataset.diff[tooltipItem['index']]}
 $${dataset.dataValue[tooltipItem['index']]} ${getDiffDirectionSymbol(
   dataset.diffValue[tooltipItem['index']],
 )} ${dataset.diffValue[tooltipItem['index']]}`
      },
    }

    return isDougnutDataCorrect ? (
      <React.Fragment>
        <DoughnutChart
          halfChart
          options={defaultDoughnutChartOptions}
          tooltipCallbacks={tooltipCallbacks}
          data={{
            labels: CHART_LABELS,
            datasets: [
              {
                label: CHART_LABEL,
                data: CHART_DATA,
                diff: CHART_DIFF,
                dataValue: CHART_DATA_VALUE,
                diffValue: CHART_DIFF_VALUE,
                backgroundColor: CHART_COLORS,
                borderColor: CHART_COLORS,
                borderWidth: 1,
              },
            ],
          }}
        />
        <div className="subblock-line">
          <span className="subblock-text">{t('building-analytics:giftCards.value')}</span>
          <span className="subblock-bold-text">${marketingData.giftCards.totalValue.value}</span>
          {marketingData.giftCards.totalValue &&
            marketingData.giftCards.totalValue.percentageDiff !== '-' && (
              <span
                className={`subblock-percent-text ${getDiffColor(
                  marketingData.giftCards.totalValue,
                )}`}
              >
                {getPercentageDiffText(marketingData.giftCards.totalValue)}
              </span>
            )}
        </div>
      </React.Fragment>
    ) : (
      <div className="no-doughnut-chart-data">
        <CloudX size={24} />
        <h3>{t('building-analytics:common.noDataTitle')}</h3>
        <p>{t('building-analytics:common.noDataDescription')}</p>
      </div>
    )
  }

  return (
    <div className="marketing-statistic-row-block">
      <h3 className="title">{t('building-analytics:giftCards.title')}</h3>
      <Can
        on={'Navbar'}
        to={'rewards'}
        feedFlag={'rewardsAvailable'}
        upsellingLink={
          <UpsellingBlocker
            type="rewards"
            text={t('building-analytics:giftCards.upsellingText')}
            wrapperClassName="marketing-statistic-row-block__upselling-blocker"
          />
        }
      >
        <React.Fragment>
          {getGiftCardsGraph()}
          {/*  //TODO: uncomment if graph not needed
          <span className="subtitle-text">Gift cards sent (Total)</span>
          <div className="marketing-statistic-row-block__bottom-wrapper">
            <h3 className="marketing-statistic-row-block__bottom-wrapper__number">
              {sendTotal && sendTotal.value ? sendTotal.value : '0'}
            </h3>
            <div
              className={`marketing-statistic-row-block__bottom-wrapper__percent-wrapper  ${getDiffColor(
                sendTotal
              )}`}
            >
              <span className="percent-text">
                {sendTotal && sendTotal.percentageDiff && sendTotal.percentageDiff !== 'NaN'
                  ? `${getDiffDirectionSymbol(sendTotal.percentageDiff)} ${
                      sendTotal.percentageDiff
                    }%`
                  : '00.00%'}
              </span>
            </div>
          </div>
          <span className="subtitle-text with-border">Gift cards sent ($ value)</span>
          <div className="marketing-statistic-row-block__bottom-wrapper">
            <h3 className="marketing-statistic-row-block__bottom-wrapper__number">
              {sendValue && sendValue.value ? sendValue.value : '0'}
            </h3>
            <div
              className={`marketing-statistic-row-block__bottom-wrapper__percent-wrapper  ${getDiffColor(
                sendValue
              )}`}
            >
              <span className="percent-text">
                {sendValue && sendValue.percentageDiff && sendValue.percentageDiff !== 'NaN'
                  ? `${getDiffDirectionSymbol(sendValue.percentageDiff)} ${
                      sendValue.percentageDiff
                    }%`
                  : '00.00%'}
              </span>
            </div>
          </div> */}
          <Link className="redirect-button" to="/dashboard/building-dashboard/rewards">
            <span>{t('building-analytics:giftCards.goToGiftCards')}</span>
            <Icon
              styles={{ width: '20px', paddingLeft: '5px' }}
              viewBox="0 0 20 20"
              icon={'arrowRight'}
              svgProps={{ stroke: '#646464', strokeLinecap: 'round', strokeLinejoin: 'round' }}
              fill="none"
            />
          </Link>
        </React.Fragment>
      </Can>
    </div>
  )
}

GiftCards.propTypes = {
  marketingData: PropTypes.object,
}
export default GiftCards
